import { useEffect, useState } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import Sidebar from './Sidebar'
import Navbar from './Navbar'
import { ensureSession } from '../api/http'
import { useTheme } from '../context/ThemeContext'
import styles from './AppLayout.module.css'

/**
 * AppLayout wraps every protected page.
 * - Confirms a session on mount before rendering any child route: a valid access
 *   token passes instantly; an expired one whose refresh cookie is still good is
 *   refreshed in place (this is what stops a tab idle past the ~15-min access-token
 *   window from being bounced to /login while its session is really still alive);
 *   only a failed refresh redirects to /login. Mid-session expiry during an API
 *   call is handled reactively by authedFetch (http.js), not here.
 * - Renders Sidebar on the left, Navbar across the top, and the current page via <Outlet />.
 * - Owns the mobile nav drawer's open state (Navbar renders the trigger,
 *   Sidebar becomes the drawer content) since both are its direct children.
 *
 * Usage in App.jsx:
 *   <Route element={<AppLayout />}>
 *     <Route path="/dashboard" element={<Dashboard />} />
 *     <Route path="/skills"    element={<Skills />} />
 *   </Route>
 */
export default function AppLayout() {
  const navigate = useNavigate()
  const location = useLocation()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const { theme } = useTheme()
  // 'checking' until ensureSession() resolves; 'ok' mounts the app; a failed
  // refresh redirects to /login and never renders the shell.
  const [authState, setAuthState] = useState('checking')

  useEffect(() => {
    let cancelled = false
    ensureSession().then(ok => {
      if (cancelled) return
      if (ok) {
        setAuthState('ok')
      } else {
        navigate('/login', { replace: true })
      }
    })
    return () => { cancelled = true }
  }, [navigate])

  // Close the drawer whenever the route changes (e.g. a nav link was tapped).
  useEffect(() => {
    setDrawerOpen(false)
  }, [location.pathname])

  // Escape closes the drawer; body scroll is locked while it's open so the
  // page underneath doesn't scroll behind the overlay.
  useEffect(() => {
    if (!drawerOpen) return
    function handleKeyDown(e) {
      if (e.key === 'Escape') setDrawerOpen(false)
    }
    document.addEventListener('keydown', handleKeyDown)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = prevOverflow
    }
  }, [drawerOpen])

  // Don't mount child routes (and fire their API calls) until the session is
  // confirmed — a child mounting first would fetch before the token is ready.
  if (authState !== 'ok') return null

  return (
    <div className={styles.shell} data-theme={theme}>
      <Sidebar open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      <div className={styles.body}>
        <Navbar open={drawerOpen} onOpenDrawer={() => setDrawerOpen(true)} />
        <main className={styles.content}>
          <Outlet />
        </main>
      </div>
    </div>
  )
}
